"use client";

import { motion, type Variants } from "framer-motion";
import type { ReactNode } from "react";
import { useSafeReducedMotion } from "@/lib/hooks/useSafeReducedMotion";
import { fadeInUp, reducedVariants } from "@/lib/motion";

type RevealProps = {
  children: ReactNode;
  variants?: Variants;
  delay?: number;
  className?: string;
  amount?: number;
  once?: boolean;
};

/**
 * Token-driven reveal wrapper. Honors `prefers-reduced-motion` by
 * collapsing to an opacity-only fade. Prefer this over bespoke
 * `motion.div` blocks inside sections so reveal timing stays uniform.
 */
export function Reveal({
  children,
  variants = fadeInUp,
  delay = 0,
  amount = 0.3,
  once = true,
  className,
}: RevealProps) {
  const prefersReduced = useSafeReducedMotion();
  const chosen = prefersReduced ? reducedVariants : variants;

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
      transition={{ delay }}
      variants={chosen}
    >
      {children}
    </motion.div>
  );
}
